import moment from "moment";
import { useEffect, useState } from "react";
import {
  checkEmpty,
  checkEmptyObj,
  checkIf,
  checkIfNull,
  checkUndefined,
  dateIntervalCheck,
  disableDayCheck,
  matchIsNumeric,
} from "../../utils";
import { AdminMealMultiLangResponsePortionUnitEnum } from "../../openapi/client";

export interface ILanguageEntity {
  en?: string;
  ar?: string;
}
export interface IValidateEntity {
  error?: boolean;
  helperText?: string;
  dirty?: boolean;
}

export interface IValidateLangEntity {
  en?: IValidateEntity;
  ar?: IValidateEntity;
}

export interface IProps {
  phoneNumber?: string;
  deliveryPhoneNumber?: string;
  fullName?: string;
  email?: string;
  externalId?: number;
  password?: string;
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  birthDate?: string;
  height?: number;
  weight?: number;
  targetWeight?: number;
  address?: { detail?: string; zone?: number; street?: number; building?: number; flat?: number };
  gender?: string;
  subscriptions?: any;
  allergens?: Set<string>;
  selection?: ILanguageEntity;
  dailyPrice?: number;
  period?: number;
  fee?: number;
  minDeliveryDaysOfWeek?: number;
  dayList?: Array<{ name: string; enabled: boolean }>;
  availableDays?: Array<string>;
  mealTypeList?: string[];
  code?: string;
  percentage?: number;
  amount?: number;
  maxDiscountAmount?: number;
  minPurchaseAmount?: number;
  startDate?: string;
  endDate?: string;
  order?: number;
}

export interface MealValidation extends IProps {
  name?: ILanguageEntity;
  description?: ILanguageEntity;
  summary?: ILanguageEntity;
  portions?: any;
  portionUnit?: AdminMealMultiLangResponsePortionUnitEnum;
  slug?: string;
  tags?: Set<string>;
  ingredients?: ILanguageEntity;
}

export interface IValidate {
  show?: boolean;
  error?: boolean;
  dirty?: boolean;
  message?: string;
  phoneNumber?: IValidateEntity;
  deliveryPhoneNumber?: IValidateEntity;
  fullName?: IValidateEntity;
  email?: IValidateEntity;
  password?: IValidateEntity;
  oldPassword?: IValidateEntity;
  newPassword?: IValidateEntity;
  confirmPassword?: IValidateEntity;
  birthDate?: IValidateEntity;
  height?: IValidateEntity;
  weight?: IValidateEntity;
  targetWeight?: IValidateEntity;
  gender?: IValidateEntity;
  address?: IValidateEntity;
  name?: IValidateLangEntity;
  selection?: IValidateLangEntity;
  description?: IValidateLangEntity;
  summary?: IValidateLangEntity;
  portions?: IValidateEntity;
  portionUnit?: IValidateEntity;
  slug?: IValidateEntity;
  tags?: IValidateEntity;
  dailyPrice?: IValidateEntity;
  period?: IValidateEntity;
  dayList?: IValidateEntity;
  availableDays?: IValidateEntity;
  mealTypeList?: IValidateEntity;
  ingredients?: IValidateLangEntity;
  allergens?: IValidateEntity;
  zone?: IValidateEntity;
  area?: IValidateEntity;
  fee?: IValidateEntity;
  minDeliveryDaysOfWeek?: IValidateEntity;
  code?: IValidateEntity;
  percentage?: IValidateEntity;
  amount?: IValidateEntity;
  minPurchaseAmount?: IValidateEntity;
  maxDiscountAmount?: IValidateEntity;
  startDate?: IValidateEntity;
  endDate?: IValidateEntity;
  order?: IValidateEntity;
}

export default function useValidation(validationInfo: MealValidation) {
  const [validation, setValidation] = useState<IValidate>({
    show: false,
    error: false,
    dirty: false,
    message: "",
  });

  const checkValidWithLanguage = (entity: string) => {
    const indexValue = entity as keyof MealValidation;
    const value = validationInfo?.[indexValue];
    const EN = checkIf(value?.en) && checkEmpty(value?.en);
    const AR = checkIf(value?.ar) && checkEmpty(value?.ar);
    return { en: !EN, ar: !AR };
  };

  useEffect(() => {
    if (!checkIf(validationInfo) || checkEmptyObj(validationInfo)) {
      setValidation((state) => ({ ...state, error: true }));
      return;
    }

    setValidation((state) => {
      let newState = { ...state } as IValidate;
      if (Object.keys(validation).length === 4) {
        let fields: any = {};
        Object.keys(validationInfo).forEach((item: string) => {
          const value = validationInfo[item as keyof MealValidation];
          const typeValue = typeof value;
          if (checkIf(value?.en) || checkIf(value?.ar)) {
            fields[item] = { en: { error: false, helperText: "" }, ar: { error: false, helperText: "" } };
          } else {
            fields[item] = { error: false, helperText: "" };
          }
        });
        newState = { ...newState, ...fields };
      }

      newState.error = false;

      if (!checkUndefined(validationInfo.fullName)) {
        if (checkEmpty(validationInfo?.fullName) || !checkIf(validationInfo?.fullName)) {
          newState.fullName!.error = true;
          newState.fullName!.helperText = "Name & Surname field cannot be empty";
          newState.error = true;
        } else {
          newState.fullName!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.birthDate) && validationInfo.birthDate !== null) {
        if (checkEmpty(validationInfo?.birthDate) || !checkIf(validationInfo?.birthDate)) {
          newState.birthDate!.error = true;
          newState.birthDate!.helperText = "Birthdate field cannot be empty";
          newState.error = true;
        } else {
          const allowedDate = !disableDayCheck(moment(validationInfo.birthDate));
          let status = false;
          let message = "";

          if (!allowedDate) {
            message = "Age is required between 9-99";
            status = true;
          }

          newState.birthDate!.error = status;
          newState.birthDate!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.height) && validationInfo.height !== null) {
        if (checkEmpty(validationInfo?.height) || !checkIf(validationInfo?.height)) {
          newState.height!.error = true;
          newState.height!.helperText = "Height field cannot be empty";
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.height!));
          if (test) {
            if (validationInfo.height! < 100) {
              message = "Height must be bigger than or equal to 100 cm";
              status = true;
            } else if (validationInfo.height! > 220) {
              message = "Height must be less than 220 cm";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Height must include numeric characters";
            status = true;
          }
          newState.height!.error = status;
          newState.height!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.weight) && validationInfo.weight !== null) {
        if (checkEmpty(validationInfo?.weight) || !checkIf(validationInfo?.weight)) {
          newState.weight!.error = true;
          newState.weight!.helperText = "Weight field cannot be empty";
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.weight!));
          if (test) {
            if (validationInfo.weight! < 30) {
              message = "Weight must be bigger than or equal to 30 kg";
              status = true;
            } else if (validationInfo.weight! > 500) {
              message = "Weight must be less than 500 kg";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Weight must include numeric characters";
            status = true;
          }
          newState.weight!.error = status;
          newState.weight!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.targetWeight) && validationInfo.targetWeight !== null) {
        if (checkEmpty(validationInfo?.targetWeight) || !checkIf(validationInfo?.targetWeight)) {
          newState.targetWeight!.error = false;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.targetWeight!));
          if (test) {
            if (validationInfo.targetWeight! < 30) {
              message = "Target weight must be bigger than or equal to 30 kg";
              status = true;
            } else if (validationInfo.targetWeight! > 500) {
              message = "Target weight must be less than 500 kg";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Target weight must include numeric characters";
            status = true;
          }
          newState.targetWeight!.error = status;
          newState.targetWeight!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.gender) && validationInfo.gender !== null) {
        if (checkEmpty(validationInfo?.gender) || !checkIf(validationInfo?.gender)) {
          newState.gender!.error = true;
          newState.gender!.helperText = "Gender field cannot be empty";
          newState.error = true;
        } else {
          newState.gender!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.portionUnit)) {
        if (checkEmpty(validationInfo?.portionUnit) || !checkIf(validationInfo?.portionUnit)) {
          newState.portionUnit!.error = true;
          newState.portionUnit!.helperText = "Portion unit field cannot be empty";
          newState.error = true;
        } else {
          newState.portionUnit!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.slug)) {
        if (checkEmpty(validationInfo?.slug) || !checkIf(validationInfo?.slug)) {
          newState.slug!.error = true;
          newState.slug!.helperText = "Slug field cannot be empty";
          newState.error = true;
        } else {
          newState.slug!.error = false;
        }
      }

      if (!checkUndefined(validationInfo.phoneNumber)) {
        newState.phoneNumber!.helperText =
          "Phone number should start with +974 and be followed by eight digits starting with 3, 5, 6 or 7 without space or 0";
        if (checkEmpty(validationInfo?.phoneNumber) || !checkIf(validationInfo?.phoneNumber)) {
          newState.phoneNumber!.error = true;
          newState.error = true;
        } else {
          const regex = /^[3567]{1}\d{7}$/;
          const test = regex.test(validationInfo.phoneNumber!.slice(4));
          const status = !test;
          newState.phoneNumber!.error = status;
          if (status) newState.error = true;
        }
      }

      if (!checkUndefined(validationInfo.deliveryPhoneNumber)) {
        newState.deliveryPhoneNumber!.helperText =
          "Phone number should start with +974 and be followed by eight digits starting with 3, 5, 6 or 7 without space or 0";
        if (
          checkEmpty(validationInfo?.deliveryPhoneNumber) ||
          !checkIf(validationInfo?.deliveryPhoneNumber) ||
          validationInfo.deliveryPhoneNumber!.trim() === "+974"
        ) {
          newState.deliveryPhoneNumber!.error = false;
        } else {
          const regex = /^[3567]{1}\d{7}$/;
          const test = regex.test(validationInfo.deliveryPhoneNumber!.slice(4));
          const status = !test;
          newState.deliveryPhoneNumber!.error = status;
          if (status) newState.error = true;
        }
      }

      if (!checkUndefined(validationInfo.email)) {
        if (checkEmpty(validationInfo?.email) || !checkIf(validationInfo?.email)) {
          newState.email!.error = true;
          newState.email!.helperText = "Please provide a correct email format";
          newState.error = true;
        } else {
          const regex = new RegExp(
            "([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|\"([]!#-[^-~ \t]|(\\[\t -~]))+\")@([!#-'*+/-9=?A-Z^-~-]+(.[!#-'*+/-9=?A-Z^-~-]+)*|[[\t -Z^-~]*])"
          );
          const test = regex.test(String(validationInfo.email));
          const status = !test;
          newState.email!.error = status;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.password)) {
        if (!checkIf(validationInfo?.password) || checkEmpty(validationInfo?.password)) {
          newState.password!.error = true;
          newState.password!.helperText = "Password field cannot be empty";
          newState.error = true;
        } else if (validationInfo.password!.length < 6) {
          newState.password!.error = true;
          newState.password!.helperText = "Password must include at least six characters";
          newState.error = true;
        } else if (validationInfo.password!.length > 20) {
          newState.password!.error = true;
          newState.password!.helperText = "Password can include twenty characters at most";
          newState.error = true;
        } else {
          newState.password!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.oldPassword)) {
        if (!checkIf(validationInfo?.oldPassword) || checkEmpty(validationInfo?.oldPassword)) {
          newState.oldPassword!.error = true;
          newState.oldPassword!.helperText = "Password field cannot be empty";
          newState.error = true;
        } else if (validationInfo.oldPassword!.length < 6) {
          newState.oldPassword!.error = true;
          newState.oldPassword!.helperText = "Password must include at least six characters";
          newState.error = true;
        } else if (validationInfo.oldPassword!.length > 20) {
          newState.oldPassword!.error = true;
          newState.oldPassword!.helperText = "Password can include twenty characters at most";
          newState.error = true;
        } else {
          newState.oldPassword!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.newPassword)) {
        if (!checkIf(validationInfo?.newPassword) || checkEmpty(validationInfo?.newPassword)) {
          newState.newPassword!.error = true;
          newState.newPassword!.helperText = "Password field cannot be empty";
          newState.error = true;
        } else if (validationInfo.newPassword!.length < 6) {
          newState.newPassword!.error = true;
          newState.newPassword!.helperText = "Password must include at least six characters";
          newState.error = true;
        } else if (validationInfo.newPassword!.length > 20) {
          newState.newPassword!.error = true;
          newState.newPassword!.helperText = "Password can include twenty characters at most";
          newState.error = true;
        } else {
          newState.newPassword!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.confirmPassword)) {
        if (checkEmpty(validationInfo?.confirmPassword) || !checkIf(validationInfo?.confirmPassword)) {
          newState.confirmPassword!.error = true;
          newState.confirmPassword!.helperText = "Confirm password field cannot be empty";
          newState.error = true;
        } else {
          newState.confirmPassword!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.password) && !checkUndefined(validationInfo.confirmPassword)) {
        if (
          checkIf(validationInfo?.password) &&
          !checkEmpty(validationInfo?.password) &&
          checkIf(validationInfo?.confirmPassword) &&
          !checkEmpty(validationInfo?.confirmPassword)
        ) {
          if (validationInfo?.password?.trim() !== validationInfo?.confirmPassword?.trim()) {
            newState.confirmPassword!.error = true;
            newState.confirmPassword!.helperText = "Confirmed password does not match the password";
            newState.error = true;
          } else {
            newState.confirmPassword!.error = false;
          }
        }
      }
      if (!checkUndefined(validationInfo.newPassword) && !checkUndefined(validationInfo.confirmPassword)) {
        if (
          checkIf(validationInfo?.newPassword) &&
          !checkEmpty(validationInfo?.newPassword) &&
          checkIf(validationInfo?.confirmPassword) &&
          !checkEmpty(validationInfo?.confirmPassword)
        ) {
          if (validationInfo?.newPassword?.trim() !== validationInfo?.confirmPassword?.trim()) {
            newState.confirmPassword!.error = true;
            newState.confirmPassword!.helperText = "Confirmed password does not match the password";
            newState.error = true;
          } else {
            newState.confirmPassword!.error = false;
          }
        }
      }
      if (!checkUndefined(validationInfo.order)) {
        if (checkEmpty(validationInfo?.order) || !checkIf(validationInfo?.order)) {
          newState.order!.error = true;
          newState.order!.helperText = "Order field cannot be empty";
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.order!));
          if (test) {
            if (validationInfo.order! < 0) {
              message = "Order can't be negative";
              status = true;
            } else if (validationInfo.order! > 99) {
              message = "Order can't be more than 99";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Order must include numeric characters";
            status = true;
          }
          newState.order!.error = status;
          newState.order!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.dailyPrice)) {
        if (checkEmpty(validationInfo?.dailyPrice) || !checkIf(validationInfo?.dailyPrice)) {
          newState.dailyPrice!.error = true;
          newState.dailyPrice!.helperText = "Daily Price field cannot be empty";
          newState.error = true;
        } else {
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          let status = Boolean(validationInfo.dailyPrice!.toString().match(numberRegex));
          newState.dailyPrice!.error = !status;
          newState.dailyPrice!.helperText = "Invalid daily price value";
          if (status) {
            const value = Number(validationInfo.dailyPrice);
            if (value < 1) {
              newState.dailyPrice!.error = true;
              newState.dailyPrice!.helperText = "Value can't be less than 1";
              newState.error = true;
            }
          } else {
            newState.error = true;
          }
        }
      }
      if (!checkUndefined(validationInfo.period)) {
        if (checkEmpty(validationInfo?.period) || !checkIf(validationInfo?.period)) {
          newState.period!.error = true;
          newState.period!.helperText = "Period field cannot be empty";
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.period!));
          if (test) {
            if (validationInfo.period! < 1) {
              message = "Period can be at least one day";
              status = true;
            } else if (validationInfo.period! > 365) {
              message = "Period can't be more than one year";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Period must include numeric characters";
            status = true;
          }
          newState.period!.error = status;
          newState.period!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.dayList)) {
        if (checkEmpty(validationInfo?.dayList) || !checkIf(validationInfo?.dayList)) {
          newState.dayList!.error = true;
          newState.dayList!.helperText = "Days list cannot be empty";
          newState.error = true;
        } else {
          let status = !validationInfo.dayList?.some((i) => i.enabled);
          console.log("status daylist", status);
          let message = "At least one selection is required";

          newState.dayList!.error = status;
          newState.dayList!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.availableDays)) {
        if (checkEmpty(validationInfo?.availableDays) || !checkIf(validationInfo?.availableDays)) {
          newState.availableDays!.error = true;
          newState.availableDays!.helperText = "Available days list cannot be empty";
          newState.error = true;
        } else {
          let status = !Boolean(validationInfo.availableDays?.length);
          let message = "At least one selection is required";

          if (Number(validationInfo?.availableDays?.length) < Number(validationInfo?.minDeliveryDaysOfWeek)) {
            status = true;
            message = "Available days count must be higher than or equal to Minimum delivery days";
          }

          newState.availableDays!.error = status;
          newState.availableDays!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.mealTypeList)) {
        if (checkEmpty(validationInfo?.mealTypeList) || !checkIf(validationInfo?.mealTypeList)) {
          newState.mealTypeList!.error = true;
          newState.mealTypeList!.helperText = "Meal type list cannot be empty";
          newState.error = true;
        } else {
          let status = !(validationInfo.mealTypeList && validationInfo.mealTypeList.length > 0);
          let message = "At least one selection is required";
          newState.mealTypeList!.error = status;
          newState.mealTypeList!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.fee)) {
        if (checkEmpty(validationInfo?.fee) || !checkIf(validationInfo?.fee)) {
          newState.fee!.error = true;
          newState.fee!.helperText = "Fee field cannot be empty";
          newState.error = true;
        } else {
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          let test = Boolean(validationInfo.fee!.toString().match(numberRegex));
          let hasError = false;
          let message = "";

          if (test) {
            if (validationInfo.fee! < 0) {
              message = "Positive fee value needed";
              hasError = true;
            } else if (validationInfo.fee! > 999) {
              hasError = true;
              message = "Fee cannot be more than 999";
            } else {
              hasError = false;
            }
          } else {
            message = "Invalid fee value";
            hasError = true;
          }

          newState.fee!.error = hasError;
          newState.fee!.helperText = message;
          if (hasError) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.minDeliveryDaysOfWeek)) {
        if (checkEmpty(validationInfo?.minDeliveryDaysOfWeek) || !checkIf(validationInfo?.minDeliveryDaysOfWeek)) {
          newState.minDeliveryDaysOfWeek!.error = true;
          newState.minDeliveryDaysOfWeek!.helperText = "Number of delivery days field cannot be empty";
          newState.error = true;
        } else {
          let status = false;
          let message = "";
          const regex = new RegExp("^[0-9]+$");
          const test = regex.test(String(validationInfo.minDeliveryDaysOfWeek!));
          if (test) {
            if (validationInfo.minDeliveryDaysOfWeek! < 1) {
              message = "Number of deliveries must be at least 1";
              status = true;
            } else if (validationInfo.minDeliveryDaysOfWeek! > 7) {
              message = "Number of deliveries can't be more than 7";
              status = true;
            } else {
              status = false;
            }
            if (Number(validationInfo?.availableDays?.length) < Number(validationInfo?.minDeliveryDaysOfWeek)) {
              status = true;
              message = "Available days count must be higher than or equal to Minimum delivery days";
            }
          } else {
            message = "Delivery number must include numeric characters";
            status = true;
          }
          newState.minDeliveryDaysOfWeek!.error = status;
          newState.minDeliveryDaysOfWeek!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.code)) {
        if (checkEmpty(validationInfo?.code) || !checkIf(validationInfo?.code)) {
          newState.code!.error = true;
          newState.code!.helperText = "Code field cannot be empty";
          newState.error = true;
        } else if (validationInfo.code!.length > 30) {
          newState.code!.error = true;
          newState.code!.helperText = "Max. 30 characters are allowed.";
          newState.error = true;
        } else {
          const regex = /^[a-zA-Z0-9]+$/;
          const test = regex.test(String(validationInfo.code));
          const status = !test;
          newState.code!.error = status;
          newState.code!.helperText = "Only numeric characters are allowed.";
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.startDate)) {
        if (checkEmpty(validationInfo?.startDate) || !checkIf(validationInfo?.startDate)) {
          newState.startDate!.error = false;
        } else {
          const allowedDate = dateIntervalCheck(moment(validationInfo.startDate), moment(validationInfo.endDate));
          let status = false;
          let message = "";

          if (!allowedDate) {
            message = "Please check start and end dates";
            status = true;
          }

          newState.startDate!.error = status;
          newState.startDate!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.endDate)) {
        if (checkEmpty(validationInfo?.endDate) || !checkIf(validationInfo?.endDate)) {
          newState.endDate!.error = false;
        } else {
          const allowedDate = dateIntervalCheck(moment(validationInfo.startDate), moment(validationInfo.endDate));
          let status = false;
          let message = "";

          if (!allowedDate) {
            message = "Please check start and end dates";
            status = true;
          }

          newState.endDate!.error = status;
          newState.endDate!.helperText = message;
          if (status) newState.error = true;
        }
      }

      if (!checkUndefined(validationInfo.percentage)) {
        // check if Amount field is full
        const amountEmpty = checkEmpty(validationInfo?.amount) || !checkIf(validationInfo?.amount);
        const amountNumeric = matchIsNumeric(validationInfo?.amount);

        if (checkEmpty(validationInfo?.percentage) || !checkIf(validationInfo?.percentage)) {
          if (amountEmpty || !amountNumeric) {
            newState.percentage!.error = true;
            newState.percentage!.helperText = "Percentage or amount needed";
            newState.amount!.error = true;
            newState.amount!.helperText = "Percentage or amount needed";
            newState.error = true;
          }
        } else {
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          let test = Boolean(validationInfo.percentage!.toString().match(numberRegex));
          let hasError = false;
          let message = "";

          if (test) {
            if (validationInfo.percentage! < 0) {
              message = "Positive value needed";
              hasError = true;
            } else if (validationInfo.percentage! > 100) {
              hasError = true;
              message = "Percentage cannot be more than 100";
            } else {
              hasError = false;
            }
          } else {
            message = "Invalid percentage value";
            hasError = true;
          }

          if (!amountEmpty) {
            hasError = true;
            message = "Amount and percentage can't be applied at the same time";
            newState.amount!.error = hasError;
            newState.amount!.helperText = message;
          } else {
            newState.amount!.error = false;
          }

          newState.percentage!.error = hasError;
          newState.percentage!.helperText = message;
          if (hasError) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.amount)) {
        // check if Percentage field is full
        const percentageEmpty = checkEmpty(validationInfo?.percentage) || !checkIf(validationInfo?.percentage);
        const percentageNumeric = matchIsNumeric(validationInfo?.percentage);

        if (checkEmpty(validationInfo?.amount) || !checkIf(validationInfo?.amount)) {
          if (percentageEmpty) {
            newState.percentage!.error = true;
            newState.percentage!.helperText = "Percentage or amount needed";
            newState.amount!.error = true;
            newState.amount!.helperText = "Percentage or amount needed";
            newState.error = true;
          }
        } else {
          const numberRegex = /^\d+(\.\d{1,3})?$/;
          let test = Boolean(validationInfo.amount!.toString().match(numberRegex));
          let hasError = false;
          let message = "";

          if (test) {
            if (validationInfo.amount! < 0) {
              message = "Positive value needed";
              hasError = true;
            } else if (validationInfo.amount! > 10000) {
              hasError = true;
              message = "Amount cannot be more than 10000";
            } else {
              hasError = false;
            }
          } else {
            message = "Invalid amount value";
            hasError = true;
          }

          if (!percentageEmpty) {
            hasError = true;
            message = "Amount and percentage can't be applied at the same time";
            newState.percentage!.error = hasError;
            newState.percentage!.helperText = message;
          } else {
            newState.percentage!.error = false;
          }

          newState.amount!.error = hasError;
          newState.amount!.helperText = message;
          if (hasError) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.maxDiscountAmount)) {
        if (checkEmpty(validationInfo?.maxDiscountAmount) || !checkIf(validationInfo?.maxDiscountAmount)) {
          newState.maxDiscountAmount!.error = false;
        } else {
          let status = false;
          let message = "";
          const regex = /^\d+(\.\d{1,3})?$/;

          // const test = regex.test(String(validationInfo.maxDiscountAmount!));
          const test = Boolean(validationInfo.maxDiscountAmount!.toString().match(regex));

          if (test) {
            if (validationInfo.maxDiscountAmount! <= 0) {
              message = "Please enter a value greater than zero";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Max Discount Amount must include numeric characters";
            status = true;
          }
          newState.maxDiscountAmount!.error = status;
          newState.maxDiscountAmount!.helperText = message;
          if (status) newState.error = true;
        }
      }
      if (!checkUndefined(validationInfo.minPurchaseAmount)) {
        if (checkEmpty(validationInfo?.minPurchaseAmount) || !checkIf(validationInfo?.minPurchaseAmount)) {
          newState.minPurchaseAmount!.error = false;
        } else {
          let status = false;
          let message = "";
          const regex = /^\d+(\.\d{1,3})?$/;
          const test = Boolean(validationInfo.minPurchaseAmount!.toString().match(regex));
          if (test) {
            if (validationInfo.minPurchaseAmount! <= 0) {
              message = "Please enter a value greater than zero";
              status = true;
            } else {
              status = false;
            }
          } else {
            message = "Min Purchase Amount must include numeric characters";
            status = true;
          }
          newState.minPurchaseAmount!.error = status;
          newState.minPurchaseAmount!.helperText = message;
          if (status) newState.error = true;
        }
      }

      /** Validation with language support */

      if (!checkUndefined(validationInfo.name)) {
        const checkValid = checkValidWithLanguage("name");

        // English name is required
        if (checkIf(validationInfo?.name?.en)) {
          if (checkValid.en) {
            newState.name!.en!.error = false;
          } else {
            newState.name!.en!.error = true;
            newState.name!.en!.helperText = "Name field cannot be empty";
            newState.error = true;
          }
        }
        // Arabic name is optional - no error required
        if (checkIf(validationInfo?.name?.ar)) {
          newState.name!.ar!.error = false;
        }
      }
      if (!checkUndefined(validationInfo.selection)) {
        const checkValid = checkValidWithLanguage("selection");

        // English selection is required
        if (checkIf(validationInfo?.selection?.en)) {
          if (checkValid.en) {
            newState.selection!.en!.error = false;
          } else {
            newState.selection!.en!.error = true;
            newState.selection!.en!.helperText = "Selection field cannot be empty";
            newState.error = true;
          }
        }
        // Arabic selection is optional - no error required
        if (checkIf(validationInfo?.selection?.ar)) {
          newState.selection!.ar!.error = false;
        }
      }

      if (!checkUndefined(validationInfo.summary)) {
        const checkValid = checkValidWithLanguage("summary");

        // English summary validation (optional - no error if empty)
        if (checkIf(validationInfo?.summary?.en)) {
          newState.summary!.en!.error = false;
        }
        // Arabic summary is optional - no error required
        if (checkIf(validationInfo?.summary?.ar)) {
          newState.summary!.ar!.error = false;
        }
      }

      if (!checkUndefined(validationInfo.description)) {
        const checkValid = checkValidWithLanguage("description");

        // English description validation (optional - no error if empty)
        if (checkIf(validationInfo?.description?.en)) {
          newState.description!.en!.error = false;
        }
        // Arabic description is optional - no error required
        if (checkIf(validationInfo?.description?.ar)) {
          newState.description!.ar!.error = false;
        }
      }

      return newState;
    });
  }, [validationInfo]);
  return { validation, setValidation };
}
